/**
 * Email Settings Page - STORY-029
 *
 * Configuration:
 * - Allowed sending hours
 * - SMTP configuration
 * - Resend API configuration
 * - Rate limiting
 * - Retry settings
 */

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Settings,
  Server,
  Clock,
  Mail,
  Shield,
  RefreshCw,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Save,
  TestTube,
  Globe,
} from 'lucide-react'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { useEmailSettings, useUpdateEmailSettings, useTestEmailSettings } from '@/hooks/useEmails'
import type { EmailSettingsFormData } from '@/types/email'

// Days of the week
const daysOfWeek = [
  { value: 0, label: 'Dim' },
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mer' },
  { value: 4, label: 'Jeu' },
  { value: 5, label: 'Ven' },
  { value: 6, label: 'Sam' },
]

// Common timezones
const timezones = [
  'Europe/Paris',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
]

// Default settings
const defaultSettings: Partial<EmailSettingsFormData> = {
  smtp_host: '',
  smtp_port: 587,
  smtp_secure: true,
  smtp_user: '',
  smtp_password: '',
  resend_api_key: null,
  use_resend: false,
  from_email: '',
  from_name: '',
  reply_to_email: null,
  sending_enabled: true,
  allowed_sending_start: '08:00',
  allowed_sending_end: '20:00',
  allowed_sending_days: [1, 2, 3, 4, 5], // Monday to Friday
  timezone: 'Europe/Paris',
  max_emails_per_hour: 100,
  max_emails_per_day: 1000,
  retry_enabled: true,
  max_retry_count: 3,
  retry_delay_minutes: 5,
}

export default function EmailSettingsPage() {
  const [formData, setFormData] = useState<Partial<EmailSettingsFormData>>(defaultSettings)
  const [showPassword, setShowPassword] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(
    null
  )
  const [hasChanges, setHasChanges] = useState(false)

  // Queries and mutations
  const { data: settings, isLoading, error } = useEmailSettings()
  const updateMutation = useUpdateEmailSettings()
  const testMutation = useTestEmailSettings()

  // Load settings into form
  useEffect(() => {
    if (settings) {
      setFormData({
        smtp_host: settings.smtp_host || '',
        smtp_port: settings.smtp_port || 587,
        smtp_secure: settings.smtp_secure ?? true,
        smtp_user: settings.smtp_user || '',
        smtp_password: settings.smtp_password || '',
        resend_api_key: settings.resend_api_key || null,
        use_resend: settings.use_resend ?? false,
        from_email: settings.from_email || '',
        from_name: settings.from_name || '',
        reply_to_email: settings.reply_to_email || null,
        sending_enabled: settings.sending_enabled ?? true,
        allowed_sending_start: settings.allowed_sending_start || '08:00',
        allowed_sending_end: settings.allowed_sending_end || '20:00',
        allowed_sending_days: settings.allowed_sending_days || [1, 2, 3, 4, 5],
        timezone: settings.timezone || 'Europe/Paris',
        max_emails_per_hour: settings.max_emails_per_hour || 100,
        max_emails_per_day: settings.max_emails_per_day || 1000,
        retry_enabled: settings.retry_enabled ?? true,
        max_retry_count: settings.max_retry_count || 3,
        retry_delay_minutes: settings.retry_delay_minutes || 5,
      })
    }
  }, [settings])

  // Update form data with change tracking
  const updateField = <K extends keyof EmailSettingsFormData>(
    field: K,
    value: EmailSettingsFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  // Toggle day in allowed days
  const toggleDay = (day: number) => {
    const currentDays = formData.allowed_sending_days || []
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day].sort()
    updateField('allowed_sending_days', newDays)
  }

  // Save settings
  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(formData as EmailSettingsFormData)
      setHasChanges(false)
    } catch (error) {
      console.error('Failed to save settings:', error)
    }
  }

  // Test email configuration
  const handleTestEmail = async () => {
    if (!testEmail) return

    setTestResult(null)
    try {
      const result = await testMutation.mutateAsync(testEmail)
      setTestResult({
        success: result.success,
        message: result.success
          ? 'Email de test envoye avec succes !'
          : result.error || 'Echec de l\'envoi',
      })
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Erreur inconnue',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-brown-600">Erreur lors du chargement des parametres</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Parametres Email
          </h1>
          <p className="text-brown-600 mt-1">
            Configurez votre serveur email et les preferences d'envoi
          </p>
        </div>
        <Button
          onClick={handleSave}
          isLoading={updateMutation.isPending}
          disabled={!hasChanges}
          leftIcon={<Save className="w-4 h-4" />}
        >
          Enregistrer
        </Button>
      </div>

      {/* Provider Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Server className="w-5 h-5 text-mint-600" />
            <CardTitle>Fournisseur Email</CardTitle>
          </div>
        </CardHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => updateField('use_resend', false)}
            className={`p-4 border rounded-lg text-left transition-colors ${
              !formData.use_resend
                ? 'border-mint-500 bg-mint-50'
                : 'border-beige-200 hover:border-beige-300'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <Server className="w-5 h-5 text-brown-600" />
              <span className="font-medium text-brown-900">SMTP</span>
            </div>
            <p className="text-sm text-brown-600">
              Utilisez votre propre serveur SMTP (Gmail, Office 365, etc.)
            </p>
          </button>

          <button
            onClick={() => updateField('use_resend', true)}
            className={`p-4 border rounded-lg text-left transition-colors ${
              formData.use_resend
                ? 'border-mint-500 bg-mint-50'
                : 'border-beige-200 hover:border-beige-300'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-5 h-5 text-brown-600" />
              <span className="font-medium text-brown-900">Resend</span>
            </div>
            <p className="text-sm text-brown-600">
              Service email moderne avec excellent taux de delivrabilite
            </p>
          </button>
        </div>
      </Card>

      {/* SMTP Configuration */}
      {!formData.use_resend && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Server className="w-5 h-5 text-mint-600" />
                <CardTitle>Configuration SMTP</CardTitle>
              </div>
            </CardHeader>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Hote SMTP"
                value={formData.smtp_host}
                onChange={(e) => updateField('smtp_host', e.target.value)}
                placeholder="smtp.gmail.com"
              />

              <Input
                label="Port"
                type="number"
                value={formData.smtp_port}
                onChange={(e) => updateField('smtp_port', parseInt(e.target.value))}
                placeholder="587"
              />

              <Input
                label="Utilisateur"
                value={formData.smtp_user}
                onChange={(e) => updateField('smtp_user', e.target.value)}
                placeholder="user@example.com"
              />

              <div className="relative">
                <Input
                  label="Mot de passe"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.smtp_password}
                  onChange={(e) => updateField('smtp_password', e.target.value)}
                  placeholder="********"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-8 text-brown-400 hover:text-brown-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <input
                type="checkbox"
                id="smtp_secure"
                checked={formData.smtp_secure}
                onChange={(e) => updateField('smtp_secure', e.target.checked)}
                className="w-4 h-4 text-mint-600 border-beige-300 rounded focus:ring-mint-500"
              />
              <label htmlFor="smtp_secure" className="text-sm text-brown-700">
                Connexion securisee (TLS/SSL)
              </label>
            </div>
          </Card>
        </motion.div>
      )}

      {/* Resend Configuration */}
      {formData.use_resend && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-mint-600" />
                <CardTitle>Configuration Resend</CardTitle>
              </div>
            </CardHeader>

            <div className="relative">
              <Input
                label="Cle API Resend"
                type={showApiKey ? 'text' : 'password'}
                value={formData.resend_api_key || ''}
                onChange={(e) => updateField('resend_api_key', e.target.value || null)}
                placeholder="re_..."
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-8 text-brown-400 hover:text-brown-600"
              >
                {showApiKey ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>

            <p className="mt-2 text-sm text-brown-500">
              Obtenez votre cle API sur{' '}
              <a
                href="https://resend.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-mint-600 hover:underline"
              >
                resend.com
              </a>
            </p>
          </Card>
        </motion.div>
      )}

      {/* Sender Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Send className="w-5 h-5 text-mint-600" />
            <CardTitle>Expediteur</CardTitle>
          </div>
        </CardHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Email expediteur"
            type="email"
            value={formData.from_email}
            onChange={(e) => updateField('from_email', e.target.value)}
            placeholder="noreply@example.com"
          />

          <Input
            label="Nom expediteur"
            value={formData.from_name}
            onChange={(e) => updateField('from_name', e.target.value)}
            placeholder="Mon Entreprise"
          />

          <Input
            label="Email de reponse (optionnel)"
            type="email"
            value={formData.reply_to_email || ''}
            onChange={(e) => updateField('reply_to_email', e.target.value || null)}
            placeholder="contact@example.com"
          />
        </div>
      </Card>

      {/* Sending Schedule */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-mint-600" />
            <CardTitle>Horaires d'envoi</CardTitle>
          </div>
        </CardHeader>

        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="sending_enabled"
              checked={formData.sending_enabled}
              onChange={(e) => updateField('sending_enabled', e.target.checked)}
              className="w-4 h-4 text-mint-600 border-beige-300 rounded focus:ring-mint-500"
            />
            <label htmlFor="sending_enabled" className="text-sm text-brown-700">
              Activer l'envoi d'emails
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Heure de debut"
              type="time"
              value={formData.allowed_sending_start}
              onChange={(e) => updateField('allowed_sending_start', e.target.value)}
            />

            <Input
              label="Heure de fin"
              type="time"
              value={formData.allowed_sending_end}
              onChange={(e) => updateField('allowed_sending_end', e.target.value)}
            />

            <div>
              <label className="block text-sm font-medium text-brown-700 mb-1">
                Fuseau horaire
              </label>
              <select
                value={formData.timezone}
                onChange={(e) => updateField('timezone', e.target.value)}
                className="w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
              >
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-brown-700 mb-2">
              Jours d'envoi autorises
            </label>
            <div className="flex gap-2">
              {daysOfWeek.map((day) => (
                <button
                  key={day.value}
                  onClick={() => toggleDay(day.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.allowed_sending_days?.includes(day.value)
                      ? 'bg-mint-600 text-white'
                      : 'bg-beige-100 text-brown-600 hover:bg-beige-200'
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Rate Limiting */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-mint-600" />
            <CardTitle>Limites d'envoi</CardTitle>
          </div>
        </CardHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Emails max par heure"
            type="number"
            value={formData.max_emails_per_hour}
            onChange={(e) => updateField('max_emails_per_hour', parseInt(e.target.value))}
            min={1}
            max={1000}
          />

          <Input
            label="Emails max par jour"
            type="number"
            value={formData.max_emails_per_day}
            onChange={(e) => updateField('max_emails_per_day', parseInt(e.target.value))}
            min={1}
            max={10000}
          />
        </div>
      </Card>

      {/* Retry Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 text-mint-600" />
            <CardTitle>Reessais automatiques</CardTitle>
          </div>
        </CardHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="retry_enabled"
              checked={formData.retry_enabled}
              onChange={(e) => updateField('retry_enabled', e.target.checked)}
              className="w-4 h-4 text-mint-600 border-beige-300 rounded focus:ring-mint-500"
            />
            <label htmlFor="retry_enabled" className="text-sm text-brown-700">
              Activer les reessais automatiques en cas d'echec
            </label>
          </div>

          {formData.retry_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nombre de reessais max"
                type="number"
                value={formData.max_retry_count}
                onChange={(e) => updateField('max_retry_count', parseInt(e.target.value))}
                min={0}
                max={10}
              />

              <Input
                label="Delai entre reessais (minutes)"
                type="number"
                value={formData.retry_delay_minutes}
                onChange={(e) =>
                  updateField('retry_delay_minutes', parseInt(e.target.value))
                }
                min={1}
                max={1440}
              />
            </div>
          )}
        </div>
      </Card>

      {/* Test Email */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <TestTube className="w-5 h-5 text-mint-600" />
            <CardTitle>Tester la configuration</CardTitle>
          </div>
        </CardHeader>

        <div className="flex gap-4">
          <div className="flex-1">
            <Input
              placeholder="Adresse email de test"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              leftIcon={<Mail className="w-4 h-4" />}
            />
          </div>
          <Button
            onClick={handleTestEmail}
            isLoading={testMutation.isPending}
            disabled={!testEmail}
            leftIcon={<Send className="w-4 h-4" />}
          >
            Envoyer un test
          </Button>
        </div>

        {testResult && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-4 p-4 rounded-lg flex items-center gap-3 ${
              testResult.success
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}
          >
            {testResult.success ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span>{testResult.message}</span>
          </motion.div>
        )}
      </Card>
    </div>
  )
}
